import { Box, CircularProgress } from "@mui/material"
import { FilterAltOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useState } from "react"

import { SalesOrderFilterChips } from "./filters/SalesOrderFilterChips"
import { SalesOrderRow } from "./SalesOrderRow"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { Button } from "@/components/common/Button"
import SearchInput from "@/components/SearchInput/SearchInput"
import type { SalesOrder } from "@/graphql/codegen/graphql"
import { StatusDateRangeFilterDrawer } from "@/screens/CustomerOrders/components/filters/StatusDateRangeFilterDrawer"
import { SALES_ORDER_DATE_RANGE_TYPE_OPTIONS, SALES_ORDER_STATUS_OPTIONS } from "@/screens/CustomerOrders/constants"
import { useSalesOrderList } from "@/screens/CustomerOrders/hooks/useSalesOrderList"

export const OpenSalesOrdersTabPanel: FC = () => {
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  // Keyed by SO number rather than id: a refetch after a save can hand back the same order under a
  // new row, and the user's open card should survive that. A number the list no longer returns
  // (its lines were regrouped into other orders) is dropped below, so it can't reopen later.
  const [expandedSalesOrderNumbers, setExpandedSalesOrderNumbers] = useState<string[]>([])
  // Rows read their server values once, so after a save the whole list is remounted once the refetch
  // lands — otherwise an order that received regrouped lines keeps showing its stale form. Unsaved
  // edits in other orders are intentionally discarded. The baseline is the list at save time.
  const [unsavedRefreshBaseline, setUnsavedRefreshBaseline] = useState<SalesOrder[] | null>(null)
  const [rowsGeneration, setRowsGeneration] = useState(0)

  const {
    salesOrders,
    isFetching,
    shipToLocationId,
    searchParams: listSearchParams,
    tableContainerRef,
    refetch,
    handleScroll,
    handleSearchChange,
    handleFiltersChange,
    handleFilterRemove,
    handleFilterClear,
  } = useSalesOrderList()

  if (!isFetching) {
    const loadedSalesOrderNumbers = new Set(salesOrders.map((salesOrder) => salesOrder.publicId))
    const stillLoadedNumbers = expandedSalesOrderNumbers.filter((number) => loadedSalesOrderNumbers.has(number))
    if (stillLoadedNumbers.length !== expandedSalesOrderNumbers.length) {
      setExpandedSalesOrderNumbers(stillLoadedNumbers)
    }
  }

  if (unsavedRefreshBaseline && !isFetching && salesOrders !== unsavedRefreshBaseline) {
    setUnsavedRefreshBaseline(null)
    setRowsGeneration((prev) => prev + 1)
  }

  const handleSalesOrderSave = (salesOrderNumber: string) => {
    setExpandedSalesOrderNumbers([salesOrderNumber])
    setUnsavedRefreshBaseline(salesOrders)
    refetch()
  }

  const handleSalesOrderExpandToggle = (salesOrderNumber: string) => {
    setExpandedSalesOrderNumbers((prev) =>
      prev.includes(salesOrderNumber)
        ? prev.filter((number) => number !== salesOrderNumber)
        : [...prev, salesOrderNumber]
    )
  }

  return (
    <Box className='flex flex-col gap-4'>
      <Box className='flex items-center justify-between gap-4'>
        <Box className='flex flex-1 items-center gap-4'>
          <SearchInput
            placeholder='Search by CPO, SO, Customer Code, Product ID'
            defaultValue={listSearchParams.query}
            onChange={(e) => handleSearchChange(e.target.value)}
            className='w-full max-w-[524px]'
            data-testid='customer-orders.so-tab.search-input'
          />
          <Button
            appearance='outlined'
            startIcon={<FilterAltOutlined />}
            onClick={() => setIsFilterOpen(true)}
            data-testid='customer-orders.so-tab.filter-button'
          >
            Filter
          </Button>
        </Box>
      </Box>

      <SalesOrderFilterChips searchParams={listSearchParams} onRemove={handleFilterRemove} />

      {isFetching && !salesOrders.length && (
        <Box className='flex items-center justify-center p-8'>
          <CircularProgress />
        </Box>
      )}

      {!isFetching && !salesOrders.length && <InformationMissingAlert title='No open sales orders found' />}

      {salesOrders.length > 0 && (
        <Box ref={tableContainerRef} onScroll={handleScroll} className='flex max-h-full flex-col gap-4 overflow-y-auto'>
          {salesOrders.map((salesOrder) => (
            <SalesOrderRow
              key={`${salesOrder.salesOrderId}-${rowsGeneration}`}
              salesOrder={salesOrder}
              shipToLocationId={shipToLocationId}
              expanded={expandedSalesOrderNumbers.includes(salesOrder.publicId)}
              onExpandToggle={() => handleSalesOrderExpandToggle(salesOrder.publicId)}
              onLinesSaved={() => handleSalesOrderSave(salesOrder.publicId)}
            />
          ))}
        </Box>
      )}

      <StatusDateRangeFilterDrawer
        open={isFilterOpen}
        searchParams={listSearchParams}
        onToggle={() => setIsFilterOpen((prev) => !prev)}
        onChange={handleFiltersChange}
        onClear={handleFilterClear}
        statusOptions={SALES_ORDER_STATUS_OPTIONS}
        dateRangeTypeOptions={SALES_ORDER_DATE_RANGE_TYPE_OPTIONS}
        testIdPrefix='customer-orders.so-filter-drawer'
      />
    </Box>
  )
}
